import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getEmployeeDetails,
    updateEmployee,
    getEmployeeTypes,
} from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_EMPLOYEES } from '../../../Routing/routes';
import EmployeeForm from './EmployeeForm';
import { useSocietyRoles, roleToOption } from '../../../hooks/useSocietyRoles';
import {
    emptyProfile,
    profileFromEmployee,
    normalizeProfileInput,
    validateProfile,
    buildProfilePayload,
    splitProfile,
} from '../../../consts/employeeProfileFields';
import {
    emptyShift,
    shiftFromEmployee,
    shiftBaseline as readShiftBaseline,
    validateShift,
    buildShiftPayload,
    splitShift,
    isPartialShiftFailure,
} from '../../../consts/employeeShiftFields';

const initialForm = {
    employee_type: '',
    role_id: '',
    designation: '',
    department: '',
    employment_type: '',
    name: '',
    username: '',
    phone: '',
    email: '',
    address: '',
    ...emptyProfile,
    ...emptyShift,
};

const EditEmployee = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const {
        employeeDetailsData,
        employeeDetailsLoading,
        updateEmployeeLoading,
        employeeTypesData,
        employeeTypesLoading,
    } = useSelector(state => state.EmployeeReducer || {});

    const [formData, setFormData] = useState(initialForm);
    const [errors, setErrors] = useState({});
    // What is stored, kept out of form state — the baseline the shift payload
    // is diffed against, so an untouched shift sends nothing at all.
    const [shiftBaseline, setShiftBaseline] = useState(null);

    useEffect(() => {
        const societyId = selectedSocietyStorer.getSocietyId();
        if (id) dispatch(getEmployeeDetails({ id, societyId }));
        dispatch(getEmployeeTypes({ societyId }));
    }, [dispatch, id]);

    useEffect(() => {
        const empId = employeeDetailsData?.employee_id ?? employeeDetailsData?.id;
        if (employeeDetailsData && String(empId) === String(id)) {
            const joinedName = employeeDetailsData.name
                || [employeeDetailsData.first_name, employeeDetailsData.last_name].filter(Boolean).join(' ').trim()
                || '';
            setFormData({
                employee_type: employeeDetailsData.employee_type_id
                    ?? employeeDetailsData.employee_type
                    ?? employeeDetailsData.type
                    ?? '',
                role_id: employeeDetailsData.role?.role_id ?? employeeDetailsData.role_id ?? '',
                designation: employeeDetailsData.designation || '',
                department: employeeDetailsData.department || '',
                employment_type: employeeDetailsData.employment_type || '',
                name: joinedName,
                username: employeeDetailsData.username || '',
                phone: employeeDetailsData.phone || '',
                email: employeeDetailsData.email || '',
                address: employeeDetailsData.address || '',
                ...profileFromEmployee(employeeDetailsData),
                ...shiftFromEmployee(employeeDetailsData),
            });
            setShiftBaseline(readShiftBaseline(employeeDetailsData));
        }
    }, [employeeDetailsData, id]);

    // Access roles of this society, for the Access Role picker.
    const { roles, loading: rolesLoading } = useSocietyRoles(selectedSocietyStorer.getSocietyId());
    const roleOptions = useMemo(() => roles.map(roleToOption), [roles]);

    const employeeTypeOptions = useMemo(() => (
        (Array.isArray(employeeTypesData) ? employeeTypesData : []).map(t => ({
            value: t.id ?? t.value ?? t.code,
            label: t.name ?? t.label ?? t.title ?? String(t.id),
        }))
    ), [employeeTypesData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: normalizeProfileInput(name, value) }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const e = {};
        if (!formData.employee_type) e.employee_type = 'Employee type is required';
        if (!formData.name.trim()) e.name = 'Full name is required';
        if (!formData.username.trim()) e.username = 'User name is required';
        else if (!/^[a-zA-Z0-9._-]{3,}$/.test(formData.username.trim())) e.username = 'Min 3 chars, letters / numbers / . _ -';
        if (!formData.phone.trim()) e.phone = 'Phone is required';
        else if (!/^\d{10}$/.test(formData.phone.trim())) e.phone = 'Phone must be exactly 10 digits';
        if (!formData.email.trim()) e.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email format';
        if (!formData.address.trim()) e.address = 'Address is required';
        Object.assign(e, validateProfile(formData));
        Object.assign(e, validateShift(formData));
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!validate()) return;

        const trimmed = formData.name.trim();
        const spaceIdx = trimmed.indexOf(' ');
        const first_name = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx);
        const last_name  = spaceIdx === -1 ? '' : trimmed.slice(spaceIdx + 1).trim();

        const { shift, rest: withoutShift } = splitShift(formData);
        const { profile, rest: base } = splitProfile(withoutShift);
        const { employee_type, name, role_id, employment_type, ...rest } = base;
        dispatch(updateEmployee({
            id,
            ...rest,
            ...buildProfilePayload(profile, { isEdit: true }),
            // Omitted entirely when the shift is untouched: on these endpoints
            // `shift_id: null` CLEARS the shift, so sending it unconditionally
            // would wipe shifts on every unrelated edit.
            ...buildShiftPayload(shift, shiftBaseline),
            first_name,
            last_name,
            employee_type_id: employee_type,
            // Left out when blank: the API rejects an empty employment_type.
            ...(employment_type ? { employment_type } : {}),
            // Only sent when it changed, so an edit never re-applies a role.
            ...(role_id && Number(role_id) !== Number(employeeDetailsData?.role?.role_id ?? employeeDetailsData?.role_id)
                ? { role_id: Number(role_id) } : {}),
            society_id: selectedSocietyStorer.getSocietyId(),
            onSuccess: () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES),
            // PUT is not transactional: a shift failure still saved the
            // employee, so stay on the form with the employee refetched rather
            // than reporting a save that did not fail.
            onError: (info) => {
                if (isPartialShiftFailure(info?.message)) {
                    dispatch(getEmployeeDetails({ id, societyId: selectedSocietyStorer.getSocietyId() }));
                }
            },
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES)}
                    className="tw-p-2 tw-rounded-md tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                >
                    <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                </button>
                <div>
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Edit Employee</h1>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Update employee details</p>
                </div>
            </div>

            {employeeDetailsLoading && !employeeDetailsData ? (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-p-12 tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                    <span className="tw-text-xs tw-text-gray-400">Loading employee...</span>
                </div>
            ) : (
                <EmployeeForm
                    formData={formData}
                    errors={errors}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES)}
                    submitting={updateEmployeeLoading}
                    mode="edit"
                    employeeTypeOptions={employeeTypeOptions}
                    employeeTypesLoading={employeeTypesLoading}
                    roleOptions={roleOptions}
                    rolesLoading={rolesLoading}
                    shiftBaseline={shiftBaseline}
                />
            )}
        </div>
    );
};

export default EditEmployee;
